import {cast, Instance, SnapshotOut, types} from "mobx-state-tree"
import {load as loadFromStorage, save as saveToStorage} from "../../utils/storage";
import {hasValue} from "../../utils/empty-check";
import {ProjectModel, ProjectSnapshot} from "../project/project";
import {ProjectFirestore} from "../../services/firestore/project-firestore";
import {generateKey} from "../../utils/random";
import {Staff} from "../staff/staff";

/**
 * Example store containing Rick and Morty characters
 */
export const ProjectStoreModel = types
    .model("ProjectStore")
    .props({
        projects: types.maybe(types.array(ProjectModel)),
        isLoadingProjects: types.optional(types.boolean, false)
    })
    .actions((self) => ({
        saveProjects: async (data: ProjectSnapshot[]) => {
            self.projects = cast(data)
            self.isLoadingProjects = false
        },
        storeProjects: async (data: ProjectSnapshot[]) => {
            await saveToStorage("projects", data)
        },
        loadProjects: async () => {
            const projects = await loadFromStorage("projects")
            if (hasValue(projects)) {
                self.projects = projects
            }
            return projects
        },
    }))
    .actions((self) => ({
        getProjects: async (businessId: string, staff: Staff) => {
            self.isLoadingProjects = true
            const projectFirestore = new ProjectFirestore()
            const result = await projectFirestore.getProjects(businessId, staff)

            if (result.kind === "ok" && result.data !== undefined) {
                self.saveProjects(result.data)
                self.storeProjects(result.data)
                return result.data
            } else {
                console.log(result.kind)
                self.isLoadingProjects = false
                return null
            }
        },
        refreshProjects: async (businessId: string, staff: Staff) => {
            const projectFirestore = new ProjectFirestore()
            const result = await projectFirestore.getProjects(businessId, staff)

            if (result.kind === "ok" && result.data !== undefined) {
                self.saveProjects(result.data)
                self.storeProjects(result.data)
            }
        },
    }))
    .actions((self) => ({
        addProject: async (formData: {
            name: string,
            walletAddress: string
        }, businessId, addFunds, staff: Staff) => {
            const projectFirestore = new ProjectFirestore()
            const result = await projectFirestore.addProject(formData, businessId, addFunds)

            if (result.kind === "ok") {
                await self.refreshProjects(businessId, staff)
                return true
            } else {
                console.log(result.kind)
                return false
            }
        },
        updateProject: async (id, formData: {
            name: string,
            walletAddress: string
        }, businessId, staff: Staff) => {
            const projectFirestore = new ProjectFirestore()
            const result = await projectFirestore.updateProject(id, formData)

            if (result.kind === "ok") {
                await self.refreshProjects(businessId, staff)
                return true
            } else {
                console.log(result.kind)
                return false
            }
        },
        revokeProjectApiKey: async (id, businessId, staff: Staff) => {
            const projectFirestore = new ProjectFirestore()
            const result = await projectFirestore.revokeProjectApiKey(id, generateKey())

            if (result.kind === "ok") {
                await self.refreshProjects(businessId, staff)
                return true
            } else {
                console.log(result.kind)
                return false
            }
        },
        deleteProject: async (id, businessId, staff: Staff) => {
            const projectFirestore = new ProjectFirestore()
            const result = await projectFirestore.deleteProject(id)

            if (result.kind === "ok") {
                await self.refreshProjects(businessId, staff)
                return true
            } else {
                console.log(result.kind)
                return false
            }
        },

    }))

type ProjectStoreType = Instance<typeof ProjectStoreModel>

export interface ProjectStore extends ProjectStoreType {
}

type ProjectStoreSnapshotType = SnapshotOut<typeof ProjectStoreModel>

export interface ProjectStoreSnapshot extends ProjectStoreSnapshotType {
}

export const createProjectStoreDefaultModel = () => types.optional(ProjectStoreModel, {})
